import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Minus, Plus } from "@digital-twins/design-system/icons";
import { Button } from "@/components/ui/button";
import {
  clampPerGroup,
  stepPerGroup,
  type PerGroupLimits,
} from "../utils/perGroupSizing";

interface RespondentStepperProps {
  id: string;
  label: string;
  value: number;
  limits: PerGroupLimits;
  onChange: (value: number) => void;
  disabled?: boolean;
  /** Inline `- n +` for cards: no visible label, small field and steps. */
  compact?: boolean;
  "data-testid"?: string;
}

/**
 * The sizing control: a big left-aligned number on the same underlined field the
 * study name uses, with the two steps at its right end.
 *
 * Interaction copied from Quick Study's `TwinsStepper`: typing is free and only
 * clamped on commit, so a half-typed value never snaps under the caret, and the
 * `+` goes inert at the ceiling instead of a sentence explaining the cap.
 */
export function RespondentStepper({
  id,
  label,
  value,
  limits,
  onChange,
  disabled = false,
  compact = false,
  "data-testid": testId = "respondent-stepper",
}: RespondentStepperProps) {
  const { t } = useTranslation("app");
  const [draft, setDraft] = useState(String(value));
  const [clampHint, setClampHint] = useState<string | null>(null);

  // The value also moves from outside the field (design resolution, a smaller
  // audience). Follow it whenever it is not mid-edit.
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = (raw: string): number => {
    const digits = raw.replace(/[^\d]/g, "");
    const requested = digits === "" ? limits.min : Number(digits);
    const next = clampPerGroup(requested, limits);
    if (digits !== "" && requested !== next) {
      setClampHint(
        t("creation.invalidRespondentsBody", {
          min: limits.min,
          max: limits.max,
        }),
      );
    } else {
      setClampHint(null);
    }
    onChange(next);
    return next;
  };

  const step = (direction: 1 | -1) => {
    const next = stepPerGroup(value, direction, limits);
    setDraft(String(next));
    onChange(next);
  };

  const field = (className: string) => (
    <input
      id={id}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={draft}
      disabled={disabled}
      aria-label={compact ? label : undefined}
      onChange={(e) => {
        setDraft(e.target.value.replace(/[^\d]/g, "").slice(0, 4));
        if (clampHint) {
          setClampHint(null);
        }
      }}
      onBlur={() => setDraft(String(commit(draft)))}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
      }}
      className={className}
      data-testid={testId}
    />
  );

  if (compact) {
    return (
      <div className="inline-flex flex-col gap-1">
        <div className="inline-flex items-center gap-1">
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-6 w-6"
          disabled={disabled || value <= limits.min}
          onClick={() => step(-1)}
          aria-label={`Decrease ${label.toLowerCase()}`}
          data-testid={`${testId}-minus`}
        >
          <Minus className="h-3 w-3" />
        </Button>
        {field(
          "h-6 w-10 rounded-md border border-border bg-transparent text-center text-xs tabular-nums text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
        )}
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-6 w-6"
          disabled={disabled || value >= limits.max}
          onClick={() => step(1)}
          aria-label={`Increase ${label.toLowerCase()}`}
          data-testid={`${testId}-plus`}
        >
          <Plus className="h-3 w-3" />
        </Button>
        </div>
        {clampHint ? (
          <p className="text-xs text-muted-foreground" data-testid={`${testId}-clamp-hint`}>
            {clampHint}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-xl">
      <label
        htmlFor={id}
        className="text-sm text-foreground dark:text-white block mb-0"
      >
        {label}
      </label>
      <div className="border-b border-border py-2 flex items-center justify-between gap-4">
        {field(
          "min-w-0 flex-1 bg-transparent text-xl tabular-nums text-foreground dark:text-white outline-none",
        )}
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-8 w-8"
            disabled={disabled || value <= limits.min}
            onClick={() => step(-1)}
            aria-label={`Decrease ${label.toLowerCase()}`}
            data-testid={`${testId}-minus`}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-8 w-8"
            disabled={disabled || value >= limits.max}
            onClick={() => step(1)}
            aria-label={`Increase ${label.toLowerCase()}`}
            data-testid={`${testId}-plus`}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {clampHint ? (
        <p className="mt-1 text-xs text-muted-foreground" data-testid={`${testId}-clamp-hint`}>
          {clampHint}
        </p>
      ) : null}
    </div>
  );
}
